/**
 * ExploreAiContext — Layout-level React Context for the Explore AI Assistant.
 * Manages AI session state, streaming, selected talents, and panel visibility.
 * Rendered at App level so the panel survives route navigation.
 *
 * @userstory US-EXP-13 Open AI Assistant from Shortlist
 * @userstory US-EXP-14 AI Assistant Conversation
 * @userstory US-EXP-20 AI Assistant — Split-View Panel
 * @userstory US-EXP-21 AI Assistant — Fixed Profile Cards
 * @userstory US-EXP-22 AI Assistant — Add / Remove Talent from Profile
 * @userstory US-EXP-23 Persistent AI Sessions
 * @userstory US-EXP-24 Copy Message to Clipboard
 * @userstory US-EXP-25 Regenerate Response
 * @userstory US-EXP-26 Edit Message & Resubmit
 */
import React, {
	createContext,
	useState,
	useContext,
	useCallback,
	useEffect,
	useRef,
	useMemo,
	type ReactNode,
} from 'react';
import { useOrganization } from './organizationContext';
import exploreService, {
	type ExploreAiSessionDto,
	type ExploreAiStreamChunk,
	type ExploreAiUsageDto,
	type ExploreTalentCardDto,
	type ExploreAiMessageDto,
	type ExploreAiSessionDetailDto,
} from '../services/api/exploreService';

// =============================================================================
// TYPES
// =============================================================================

export interface AiMessage {
	id?: string;
	role: string;
	content: string;
	messageIndex?: number;
}

/** Minimal talent info stored in the context for mini-cards */
export interface AiTalentCard {
	talentUserId: string;
	fullName: string;
	photoUrl: string | null;
}

export interface ExploreAiContextType {
	// Panel state
	panelOpen: boolean;
	openPanel: () => void;
	closePanel: () => void;

	// Session
	session: ExploreAiSessionDto | null;
	messages: AiMessage[];
	input: string;
	setInput: (value: string) => void;
	streaming: boolean;
	usage: ExploreAiUsageDto | null;

	// Actions
	startSession: (talentUserIds: string[], talentCards: AiTalentCard[]) => Promise<void>;
	sendMessage: () => Promise<void>;
	stopStreaming: () => void;
	resetSession: () => void;
	loadUsage: () => Promise<void>;

	// Session persistence (US-EXP-23)
	sessionList: ExploreAiSessionDto[];
	loadSessions: () => Promise<void>;
	resumeSession: (sessionId: string) => Promise<void>;
	deleteSession: (sessionId: string) => Promise<void>;
	showSessionList: boolean;
	setShowSessionList: (show: boolean) => void;

	// Message actions (US-EXP-25, US-EXP-26)
	regenerateLastResponse: () => Promise<void>;
	editAndResubmit: (messageIndex: number, newContent: string) => Promise<void>;

	// Selected talents (US-EXP-22)
	selectedTalentIds: string[];
	talentCards: AiTalentCard[];
	addTalent: (card: AiTalentCard) => void;
	removeTalent: (talentUserId: string) => void;
	isTalentInSession: (talentUserId: string) => boolean;

	// Resize (US-EXP-20)
	panelWidth: number;
	setPanelWidth: (width: number) => void;
	panelExpanded: boolean;
	togglePanelWidth: () => void;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const AI_PANEL_DEFAULT_WIDTH = 500;
const AI_PANEL_MIN_WIDTH = 350;
const AI_PANEL_MAX_RATIO = 0.6;

// =============================================================================
// CONTEXT
// =============================================================================

const ExploreAiContext = createContext<ExploreAiContextType | undefined>(undefined);

// =============================================================================
// PROVIDER
// =============================================================================

/** @userstory US-EXP-20 */
export const ExploreAiProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const { selectedOrganization } = useOrganization();
	const orgId = selectedOrganization?.id;

	// Panel visibility
	const [panelOpen, setPanelOpen] = useState(false);

	// Session state
	const [session, setSession] = useState<ExploreAiSessionDto | null>(null);
	const [messages, setMessages] = useState<AiMessage[]>([]);
	const [input, setInput] = useState('');
	const [streaming, setStreaming] = useState(false);
	const [usage, setUsage] = useState<ExploreAiUsageDto | null>(null);
	const abortRef = useRef<(() => void) | null>(null);
	const loadUsageRef = useRef<() => void>(() => {});

	// Session list (US-EXP-23)
	const [sessionList, setSessionList] = useState<ExploreAiSessionDto[]>([]);
	const [showSessionList, setShowSessionList] = useState(false);

	// Selected talents
	const [selectedTalentIds, setSelectedTalentIds] = useState<string[]>([]);
	const [talentCards, setTalentCards] = useState<AiTalentCard[]>([]);

	// Resize
	const [panelWidth, setPanelWidth] = useState(AI_PANEL_DEFAULT_WIDTH);
	const [panelExpanded, setPanelExpanded] = useState(false);

	// =========================================================================
	// Panel open/close
	// =========================================================================

	const openPanel = useCallback(() => {
		setPanelOpen(true);
	}, []);

	const closePanel = useCallback(() => {
		abortRef.current?.();
		setPanelOpen(false);
		// Don't reset session — user can reopen and resume
	}, []);

	// =========================================================================
	// Session management
	// =========================================================================

	/** @userstory US-EXP-13 */
	const startSession = useCallback(
		async (talentUserIds: string[], cards: AiTalentCard[]) => {
			if (!orgId || talentUserIds.length < 1) return;
			const sessionData = await exploreService.createAiSession(orgId, { talentUserIds });
			setSession(sessionData);
			setMessages([]);
			setSelectedTalentIds(talentUserIds);
			setTalentCards(cards);
			setPanelOpen(true);
		},
		[orgId],
	);

	/** @userstory US-EXP-14 */
	const sendMessage = useCallback(async () => {
		if (!orgId || !session || !input.trim() || streaming) return;
		const content = input.trim();
		setInput('');
		setMessages((prev) => [...prev, { role: 'user', content, messageIndex: prev.length }]);
		setStreaming(true);

		let assistantContent = '';
		setMessages((prev) => [...prev, { role: 'assistant', content: '', messageIndex: prev.length }]);

		abortRef.current = exploreService.sendAiMessageStream(
			orgId,
			session.sessionId,
			content,
			(chunk: ExploreAiStreamChunk) => {
				if (chunk.type === 'text_delta' && chunk.content) {
					assistantContent += chunk.content;
					setMessages((prev) => {
						const updated = [...prev];
						updated[updated.length - 1] = {
							...updated[updated.length - 1],
							content: assistantContent,
						};
						return updated;
					});
				} else if (chunk.type === 'done') {
					setStreaming(false);
					// Refresh usage counter after each message
					loadUsageRef.current();
				}
			},
			(error: string) => {
				setMessages((prev) => [...prev.slice(0, -1), { role: 'error', content: error }]);
				setStreaming(false);
			},
		);
	}, [orgId, session, input, streaming]);

	const stopStreaming = useCallback(() => {
		abortRef.current?.();
		setStreaming(false);
	}, []);

	const resetSession = useCallback(() => {
		abortRef.current?.();
		setSession(null);
		setMessages([]);
		setInput('');
		setStreaming(false);
		setSelectedTalentIds([]);
		setTalentCards([]);
	}, []);

	/** @userstory US-EXP-16 */
	const loadUsage = useCallback(async () => {
		if (!orgId) return;
		try {
			const data = await exploreService.getAiUsage(orgId);
			setUsage(data);
		} catch {
			// silently fail
		}
	}, [orgId]);

	// Keep ref in sync so sendMessage can call it without circular deps
	loadUsageRef.current = loadUsage;

	// =========================================================================
	// Session persistence (US-EXP-23)
	// =========================================================================

	/** @userstory US-EXP-23 */
	const loadSessions = useCallback(async () => {
		if (!orgId) return;
		try {
			const sessions = await exploreService.getAiSessions(orgId);
			setSessionList(sessions);
			setShowSessionList(true);
		} catch {
			// silently fail
		}
	}, [orgId]);

	/** @userstory US-EXP-23 */
	const resumeSession = useCallback(
		async (sessionId: string) => {
			if (!orgId) return;
			try {
				const detail = await exploreService.getAiSessionById(orgId, sessionId);
				setSession({
					sessionId: detail.sessionId,
					title: detail.title,
					profileCount: detail.profileCount,
					createdAt: detail.createdAt,
					lastInteractionAt: detail.lastInteractionAt,
					messageCount: detail.messages.length,
				});
				setMessages(
					detail.messages.map((m) => ({
						id: m.id,
						role: m.role,
						content: m.content,
						messageIndex: m.messageIndex,
					})),
				);
				setSelectedTalentIds(detail.talentUserIds);
				// We don't have full talent card info from the API — cards stay empty
				// until reconciled by the feed (future improvement)
				setTalentCards([]);
				setShowSessionList(false);
			} catch {
				// silently fail
			}
		},
		[orgId],
	);

	/** @userstory US-EXP-23 */
	const deleteSession = useCallback(
		async (sessionId: string) => {
			if (!orgId) return;
			try {
				await exploreService.deleteAiSession(orgId, sessionId);
				setSessionList((prev) => prev.filter((s) => s.sessionId !== sessionId));
				if (session?.sessionId === sessionId) {
					setSession(null);
					setMessages([]);
					setSelectedTalentIds([]);
					setTalentCards([]);
				}
			} catch {
				// silently fail
			}
		},
		[orgId, session],
	);

	// =========================================================================
	// Regenerate & Edit (US-EXP-25, US-EXP-26)
	// =========================================================================

	/** @userstory US-EXP-25 */
	const regenerateLastResponse = useCallback(async () => {
		if (!orgId || !session || streaming) return;
		// Find the last user message
		const lastUserIdx = [...messages].reverse().findIndex((m) => m.role === 'user');
		if (lastUserIdx === -1) return;
		const actualIdx = messages.length - 1 - lastUserIdx;
		const lastUserMsg = messages[actualIdx];
		const replayFromIndex = lastUserMsg.messageIndex ?? actualIdx;

		// Truncate messages to just before the assistant response
		setMessages((prev) => prev.slice(0, actualIdx + 1));
		setStreaming(true);

		let assistantContent = '';
		setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

		abortRef.current = exploreService.sendAiMessageStream(
			orgId,
			session.sessionId,
			lastUserMsg.content,
			(chunk) => {
				if (chunk.type === 'text_delta' && chunk.content) {
					assistantContent += chunk.content;
					setMessages((prev) => {
						const updated = [...prev];
						updated[updated.length - 1] = { role: 'assistant', content: assistantContent };
						return updated;
					});
				} else if (chunk.type === 'done') {
					setStreaming(false);
				}
			},
			(error) => {
				setMessages((prev) => [...prev.slice(0, -1), { role: 'error', content: error }]);
				setStreaming(false);
			},
			replayFromIndex,
		);
	}, [orgId, session, streaming, messages]);

	/** @userstory US-EXP-26 */
	const editAndResubmit = useCallback(
		async (messageIndex: number, newContent: string) => {
			if (!orgId || !session || streaming || !newContent.trim()) return;

			// Find the message at the given index and truncate everything after it
			const msgIdx = messages.findIndex((m) => (m.messageIndex ?? 0) >= messageIndex);
			const truncateAt = msgIdx >= 0 ? msgIdx : messages.length;
			const replayFromIndex = messageIndex;

			setMessages((prev) => [
				...prev.slice(0, truncateAt),
				{ role: 'user', content: newContent.trim() },
			]);
			setStreaming(true);

			let assistantContent = '';
			setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

			abortRef.current = exploreService.sendAiMessageStream(
				orgId,
				session.sessionId,
				newContent.trim(),
				(chunk) => {
					if (chunk.type === 'text_delta' && chunk.content) {
						assistantContent += chunk.content;
						setMessages((prev) => {
							const updated = [...prev];
							updated[updated.length - 1] = {
								role: 'assistant',
								content: assistantContent,
							};
							return updated;
						});
					} else if (chunk.type === 'done') {
						setStreaming(false);
					}
				},
				(error) => {
					setMessages((prev) => [
						...prev.slice(0, -1),
						{ role: 'error', content: error },
					]);
					setStreaming(false);
				},
				replayFromIndex,
			);
		},
		[orgId, session, streaming, messages],
	);

	// =========================================================================
	// Talent add/remove (US-EXP-22)
	// =========================================================================

	const addTalent = useCallback((card: AiTalentCard) => {
		setSelectedTalentIds((prev) => {
			if (prev.includes(card.talentUserId) || prev.length >= 15) return prev;
			return [...prev, card.talentUserId];
		});
		setTalentCards((prev) => {
			if (prev.some((c) => c.talentUserId === card.talentUserId)) return prev;
			return [...prev, card];
		});
	}, []);

	const removeTalent = useCallback((talentUserId: string) => {
		setSelectedTalentIds((prev) => prev.filter((id) => id !== talentUserId));
		setTalentCards((prev) => prev.filter((c) => c.talentUserId !== talentUserId));
	}, []);

	const isTalentInSession = useCallback(
		(talentUserId: string) => selectedTalentIds.includes(talentUserId),
		[selectedTalentIds],
	);

	// =========================================================================
	// Resize (US-EXP-20)
	// =========================================================================

	const togglePanelWidth = useCallback(() => {
		if (panelExpanded) {
			setPanelWidth(AI_PANEL_DEFAULT_WIDTH);
			setPanelExpanded(false);
		} else {
			setPanelWidth(Math.round(window.innerWidth * 0.5));
			setPanelExpanded(true);
		}
	}, [panelExpanded]);

	// Cleanup abort on unmount
	useEffect(() => {
		return () => {
			abortRef.current?.();
		};
	}, []);

	// =========================================================================
	// Context value
	// =========================================================================

	const value = useMemo<ExploreAiContextType>(
		() => ({
			panelOpen,
			openPanel,
			closePanel,
			session,
			messages,
			input,
			setInput,
			streaming,
			usage,
			startSession,
			sendMessage,
			stopStreaming,
			resetSession,
			loadUsage,
			sessionList,
			loadSessions,
			resumeSession,
			deleteSession,
			showSessionList,
			setShowSessionList,
			regenerateLastResponse,
			editAndResubmit,
			selectedTalentIds,
			talentCards,
			addTalent,
			removeTalent,
			isTalentInSession,
			panelWidth,
			setPanelWidth,
			panelExpanded,
			togglePanelWidth,
		}),
		[
			panelOpen,
			openPanel,
			closePanel,
			session,
			messages,
			input,
			streaming,
			usage,
			startSession,
			sendMessage,
			stopStreaming,
			resetSession,
			loadUsage,
			sessionList,
			loadSessions,
			resumeSession,
			deleteSession,
			showSessionList,
			setShowSessionList,
			regenerateLastResponse,
			editAndResubmit,
			selectedTalentIds,
			talentCards,
			addTalent,
			removeTalent,
			isTalentInSession,
			panelWidth,
			panelExpanded,
			togglePanelWidth,
		],
	);

	return <ExploreAiContext.Provider value={value}>{children}</ExploreAiContext.Provider>;
};

// =============================================================================
// HOOK
// =============================================================================

export const useExploreAi = (): ExploreAiContextType => {
	const context = useContext(ExploreAiContext);
	if (!context) {
		throw new Error('useExploreAi must be used within an ExploreAiProvider');
	}
	return context;
};

export default ExploreAiContext;
